/**
 * Central media registry — final branded Higgsfield bundle (2026-10 verified
 * Dubai set, 25 assets) served from /media/motion/{slug}/.
 * Each entry: full film (`video`, detail/hero surfaces), lightweight card
 * preview (`preview`), and poster frame (`poster`, LCP priority).
 * The 20% "TIME" wordmark is baked into the films — never add overlays.
 * `visualization: true` renders the discreet "Design visualization" label so
 * generated scenes are never presented as completed client projects.
 */
const M = (slug) => ({
  video: `/media/motion/${slug}/${slug}.mp4`,
  preview: `/media/motion/${slug}/${slug}-preview.mp4`,
  poster: `/media/motion/${slug}/${slug}.webp`,
  visualization: true,
});
// Hero keys get a dedicated 1280x720 mobile encode (CRF24, silent, faststart)
// so tall mobile hero crops stay sharp; 640 previews remain for cards.
const MH = (slug) => ({ ...M(slug), mobileHero: `/media/motion/${slug}/${slug}-mobile.mp4` });
// Revision Oct-1 bundle (flat files under /media/revision-oct1).
const R = (slug) => ({
  video: `/media/revision-oct1/${slug}.mp4`,
  preview: `/media/revision-oct1/${slug}-preview.mp4`,
  poster: `/media/revision-oct1/${slug}.webp`,
  visualization: true,
});

const O5 = (slug) => ({ video: `/media/revision-oct5/${slug}.mp4`, preview: `/media/revision-oct5/${slug}-preview.mp4`, mobileHero: `/media/revision-oct5/${slug}-preview.mp4`, poster: `/media/revision-oct5/${slug}.webp`, visualization: true });

export const MEDIA = {
  // ---- Curtain & blind style guides (10s silent loops) ----
  "style-ripple-fold": M("ripple-fold"),
  "style-m-track": M("m-track"),
  "style-pinch-pleat": M("pinch-pleat"),
  "style-two-pleat": M("two-pleat"),
  "style-double-sheer": M("double-sheer"),
  "style-blackout": M("blackout"),
  "style-sheer": M("sheer"),
  "style-double-layer": M("double-layer"),
  "style-roller": M("roller"),
  "style-zebra": M("zebra"),
  "style-roman": M("roman"),
  "style-vertical": M("vertical"),
  "style-motorized": M("motorized"),

  // ---- Curtains cinematic hero (35s continuous bedtime and lighting sequence: daylight
  // opening, bedside lamp + cove switch-off, night, morning) ----
  "curtain-day-night": O5("dubai-curtains-natural-light-time-furniture-20261005"),

  // ---- Home hero (30s villa renovation process; full-frame mobile film) ----
  "home-hero": {
    video: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005.mp4",
    preview: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005-mobile.mp4",
    mobileHero: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005-mobile.mp4",
    poster: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005.webp",
    mobilePoster: "/media/turnkey-revision-oct5/time-furniture-dubai-full-turnkey-renovation-20261005-mobile.webp",
    mobileBreakpoint: 1023,
    visualization: true,
  },

  // ---- HIGOLD accessory motion clips (8s portrait 720x1280) ----
  "higold-organizer": R("higold-wardrobe-organizer-motion-time-furniture-20261001"),
  "higold-trouser-rack": R("higold-trouser-rack-motion-time-furniture-20261001"),
  "higold-pull-down-rail": R("higold-pull-down-rail-motion-time-furniture-20261001"),

  // ---- Joinery cinematic hero (18s: room reveal → joinery details) ----
  "joinery-hero": R("custom-joinery-dubai-cinematic-time-furniture-20261001"),

  // ---- Sofa craftsmanship hero (30s: ply cutting → frame → foam → sewing → upholstery → finished sofa) ----
  "sofa-hero": R("custom-sofa-craftsmanship-time-furniture-20261001"),

  // ---- Holiday homes hero (32s: consultation → installation team → guest-ready rooms) ----
  "holiday-hero": R("dubai-holiday-home-furnishing-process-time-furniture-20261001"),

  // ---- Kitchen ----
  "kitchen-hero": MH("kitchen-hero"),
  "kitchen-renovation": M("kitchen-renovation"),
  "pantry-storage": M("pantry-storage"),
  "kitchen-bronze": M("kitchen-bronze"),
  "kitchen-silver": M("kitchen-silver"),
  "kitchen-champagne": M("kitchen-champagne"),
  "kitchen-surfaces": M("kitchen-surfaces"),

  // ---- Wardrobes ----
  "wardrobe-hero": MH("wardrobe-hero"),
  "wardrobe-accessories": M("wardrobe-accessories"),
  "wardrobe-sliding": M("wardrobe-sliding"),
  "wardrobe-fitted": M("wardrobe-fitted"),

  // ---- Ongoing project showcases (owner films, 10s each) ----
  "project-golf": M("project-golf"),
  "project-murooj": M("project-murooj"),
  "project-mleiha": M("project-mleiha"),
  "project-samara": M("project-samara"),
  "project-villa-exterior": M("project-villa-exterior"),
  "project-palm": M("project-palm"),
};

export function getMedia(key) {
  return MEDIA[key] || null;
}
